import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useSearchParams } from "react-router";

import { FormattedDateTime } from "@/shared/datetime";
import type { MetricRecord } from "@/shared/domain/metrics";
import { useMetricsBySession } from "@/shared/domain/metrics/hooks";
import {
  ancestorPaths,
  buildMetricTree,
  latestRecord,
  walk,
  type MetricTreeIndex,
  type MetricTreeNode,
} from "@/shared/domain/metrics/metricTree";
import { SearchParams } from "@/shared/environment";
import { Button } from "@/shared/foundation/primitives";
import {
  isSubstringMatch,
  substringFilter,
} from "@/shared/text/substringFilter";
import { SubstringHighlight } from "@/shared/text/SubstringHighlight";

import { useSessionOutletContext } from "../useSessionOutletContext";

import { MetricDetailCard } from "./MetricDetailCard";
import styles from "./SessionMetricsTab.module.css";

const EM_DASH = "—";

/**
 * Selection chips shown by the group picker before the rest collapse into a
 * "+N" count. The chip `max-width` in the module CSS is sized so this many
 * width-capped chips, the count, and the text caret share one line.
 */
const MAX_VISIBLE_CHIPS = 1;

/** Append the toolbar's 0.875rem font class to an (optional) className. */
const withInputText = (className?: string) =>
  `${className ?? ""} ${styles.inputText}`.trim();

/** The session metric tree carries each node's published records as its payload. */
type SessionMetricIndex = MetricTreeIndex<MetricRecord[]>;
type SessionMetricNode = MetricTreeNode<MetricRecord[]>;

/**
 * "Metrics" tab on the session-detail surface. Lists every metric attached
 * to the session named in the URL (`/sessions/:sessionId/metrics`) as a
 * tree-shaped table: metric names are split on `.` and grouped under
 * collapsible parent rows so `imu.accel.x` and `imu.accel.y` share an
 * `imu.accel` branch.
 *
 * Tab is mounted only when the `release-metrics` feature flag is on.
 *
 * The toolbar provides three controls:
 *   1. A free-text **search** input that substring-filters the tree.
 *   2. A **group** dropdown that narrows to a single top-level prefix.
 *   3. An **expand/collapse all** button.
 *
 * Tree state lives in this component (a `Set` of expanded ids) rather than the
 * URL — drill-state is ephemeral and not worth surviving a navigation.
 *
 * Clicking a row that is a published metric selects it and opens a
 * `MetricDetailCard` to the right of the table (sticky to the scroll position).
 * The selection is held as a metric id (its dotted path) so it survives a tree
 * rebuild on data refetch.
 */
export function SessionMetricsTab() {
  const { session } = useSessionOutletContext();
  const sessionId = session.session_id;
  const orgId = session.org_id;

  const { data, isLoading, error } = useMetricsBySession(sessionId, orgId);

  // Unique metric names + their records, derived once from the session's
  // records. Names drive the tree structure; records become each node's payload.
  const { names, recordsByName } = React.useMemo(() => {
    const byName = new Map<string, MetricRecord[]>();
    for (const record of data ?? []) {
      const existing = byName.get(record.name);
      if (existing) {
        existing.push(record);
      } else {
        byName.set(record.name, [record]);
      }
    }
    return { names: [...byName.keys()], recordsByName: byName };
  }, [data]);

  const dataForPath = React.useCallback(
    (path: string): MetricRecord[] => recordsByName.get(path) ?? [],
    [recordsByName],
  );

  // -- Toolbar filter state --------------------------------------------------

  const [searchText, setSearchText] = React.useState("");
  const [groupPrefixes, setGroupPrefixes] = React.useState<string[]>([]);

  // Unique first-segment prefixes, sorted, for the group dropdown.
  const groupOptions = React.useMemo(() => {
    const prefixes = new Set<string>();
    for (const name of names) {
      prefixes.add(name.split(".")[0]);
    }
    return [...prefixes].sort((a, b) => a.localeCompare(b));
  }, [names]);

  // Apply the group-prefix filter, then the search filter, to the flat name
  // list — then rebuild the index from the survivors. Matching leaf names by
  // substring reconstructs the same pruned subtree as matching every node: a
  // query that's a substring of a branch path is also a substring of every
  // leaf beneath it.
  const filteredNames = React.useMemo(() => {
    let result = names;
    if (groupPrefixes.length > 0) {
      const selected = new Set(groupPrefixes);
      result = result.filter((name) => selected.has(name.split(".")[0]));
    }
    if (searchText.trim() !== "") {
      result = result.filter((name) => isSubstringMatch(searchText, name));
    }
    return result;
  }, [names, groupPrefixes, searchText]);

  const filteredIndex = React.useMemo<SessionMetricIndex>(
    () => buildMetricTree(filteredNames, dataForPath),
    [filteredNames, dataForPath],
  );

  // Total records in each node's subtree — the "Records" badge the Session tab
  // shows on branches. This is distinct from the structural `leafCount` (number
  // of metrics) the filter shows; we derive it from the per-node `data` the
  // index already carries, so the shared builder stays generic.
  const recordCountById = React.useMemo(() => {
    const counts = new Map<string, number>();
    const accumulate = (id: string): number => {
      const node = filteredIndex.getItem(id);
      if (node === undefined) {
        return 0;
      }
      let total = node.data.length;
      for (const childId of filteredIndex.getChildren(id)) {
        total += accumulate(childId);
      }
      counts.set(id, total);
      return total;
    };
    for (const rootId of filteredIndex.getChildren(filteredIndex.rootItemId)) {
      accumulate(rootId);
    }
    return counts;
  }, [filteredIndex]);

  // -- Expansion state -------------------------------------------------------

  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const toggle = React.useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Ids of every branch (node with children) in the *filtered* tree.
  const branchPaths = React.useMemo(() => {
    const paths: string[] = [];
    walk(filteredIndex, filteredIndex.rootItemId, (node) => {
      if (node.hasChildren) {
        paths.push(node.id);
      }
    });
    return paths;
  }, [filteredIndex]);

  const expandAll = React.useCallback(
    () => setExpanded(new Set(branchPaths)),
    [branchPaths],
  );

  const collapseAll = React.useCallback(() => setExpanded(new Set()), []);

  const allExpanded =
    branchPaths.length > 0 && branchPaths.every((path) => expanded.has(path));

  // When a search is active, auto-expand every branch in the filtered tree so
  // matching leaves are immediately visible. The expansion state from before
  // the search is saved and restored when the search is cleared.
  const preSearchExpanded = React.useRef<Set<string> | null>(null);

  React.useEffect(() => {
    const isSearching = searchText.trim() !== "";
    if (isSearching) {
      if (preSearchExpanded.current === null) {
        // Capture state on first non-empty search keystroke.
        setExpanded((prev) => {
          preSearchExpanded.current = prev;
          return new Set(branchPaths);
        });
      } else {
        setExpanded(new Set(branchPaths));
      }
    } else if (preSearchExpanded.current !== null) {
      // Restore saved state when search is cleared.
      setExpanded(preSearchExpanded.current);
      preSearchExpanded.current = null;
    }
  }, [searchText, branchPaths]);

  // -- Selection state (URL-driven) ------------------------------------------

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPath = searchParams.get(SearchParams.SELECTED_METRIC);

  const selectedNode = React.useMemo(
    () => (selectedPath ? filteredIndex.getItem(selectedPath) : undefined),
    [filteredIndex, selectedPath],
  );

  const selectNode = React.useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (next.get(SearchParams.SELECTED_METRIC) === id) {
            next.delete(SearchParams.SELECTED_METRIC);
          } else {
            next.set(SearchParams.SELECTED_METRIC, id);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearSelection = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SearchParams.SELECTED_METRIC);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Auto-expand ancestors of a deep-linked selection.
  React.useEffect(() => {
    if (!selectedPath) {
      return;
    }
    const ancestors = ancestorPaths(selectedPath);
    if (ancestors.length === 0) {
      return;
    }
    setExpanded((prev) => {
      if (ancestors.every((path) => prev.has(path))) {
        return prev;
      }
      const next = new Set(prev);
      for (const path of ancestors) {
        next.add(path);
      }
      return next;
    });
  }, [selectedPath]);

  // -- Render ----------------------------------------------------------------

  const hasMetrics = !isLoading && !error && names.length > 0;

  return (
    <div className={styles.root}>
      {hasMetrics && (
        <div className={styles.toolbar}>
          <TextField
            size="small"
            margin="dense"
            type="search"
            placeholder="Search metrics..."
            aria-label="Search metrics"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={styles.searchInput}
            InputLabelProps={{ className: styles.inputText }}
            InputProps={{
              className: styles.inputText,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Autocomplete
            multiple
            size="small"
            autoHighlight
            options={groupOptions}
            value={groupPrefixes}
            onChange={(_event, next) => setGroupPrefixes(next)}
            filterOptions={(opts, { inputValue }) =>
              substringFilter(inputValue, opts).sort(
                (a, b) =>
                  Number(groupPrefixes.includes(b)) -
                  Number(groupPrefixes.includes(a)),
              )
            }
            className={styles.groupSelect}
            slotProps={{
              popper: {
                className: styles.groupPopper,
                placement: "bottom-start",
                modifiers: [
                  { name: "flip", options: { flipVariations: false } },
                ],
              },
            }}
            renderTags={(selected, getTagProps) => (
              <>
                {selected.slice(0, MAX_VISIBLE_CHIPS).map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      size="small"
                      label={option}
                      {...chipProps}
                    />
                  );
                })}
                {selected.length > MAX_VISIBLE_CHIPS && (
                  <span
                    className={`MuiAutocomplete-tag ${styles.overflowCount}`}
                  >
                    +{selected.length - MAX_VISIBLE_CHIPS}
                  </span>
                )}
              </>
            )}
            renderOption={(props, option, { selected }) => (
              <li key={option} {...props}>
                <Checkbox
                  size="small"
                  checked={selected}
                  tabIndex={-1}
                  disableRipple
                  className={styles.optionCheckbox}
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Group"
                placeholder={groupPrefixes.length ? "" : "All groups"}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  className: withInputText(params.InputLabelProps?.className),
                }}
                InputProps={{
                  ...params.InputProps,
                  className: withInputText(params.InputProps?.className),
                }}
              />
            )}
          />

          {branchPaths.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              className={styles.expandCollapseButton}
              startIcon={
                allExpanded ? (
                  <UnfoldLessIcon fontSize="small" />
                ) : (
                  <UnfoldMoreIcon fontSize="small" />
                )
              }
              onClick={allExpanded ? collapseAll : expandAll}
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </Button>
          )}
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.tableArea}>
          {isLoading && (
            <Box className={styles.loading}>
              <CircularProgress size={24} />
            </Box>
          )}
          {error && (
            <Alert severity="error">
              Failed to load metrics for this session: {error.message}
            </Alert>
          )}
          {!isLoading && !error && names.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No metrics have been published to this session yet.
            </Typography>
          )}
          {hasMetrics && filteredNames.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No metrics match the current filters.
            </Typography>
          )}
          {hasMetrics && filteredNames.length > 0 && (
            <TableContainer>
              <Table size="small" aria-label="session metrics">
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell align="right">Records</TableCell>
                    <TableCell align="right">Latest value</TableCell>
                    <TableCell>Last published</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredIndex
                    .getChildren(filteredIndex.rootItemId)
                    .flatMap((rootId) =>
                      renderRows(
                        filteredIndex,
                        rootId,
                        recordCountById,
                        expanded,
                        toggle,
                        selectedPath,
                        selectNode,
                        searchText,
                      ),
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>

        {selectedNode && (
          <div className={styles.detailPane}>
            <MetricDetailCard
              node={selectedNode}
              orgId={orgId}
              onClose={clearSelection}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Small magnifying-glass icon for the search adornment, avoids pulling in a
 *  full icon package for a single glyph. Matches the 20×20 sizing MUI
 *  `InputAdornment` expects for "small" TextFields. */
function SearchIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/**
 * Render the row for `id` and, when it is expanded, its descendants — walking
 * the index by id (`getItem` for the payload, `getChildren` to recurse).
 */
function renderRows(
  index: SessionMetricIndex,
  id: string,
  recordCountById: Map<string, number>,
  expanded: Set<string>,
  toggle: (id: string) => void,
  selectedPath: string | null,
  onSelect: (id: string) => void,
  searchText: string,
): React.ReactElement[] {
  const node = index.getItem(id);
  if (node === undefined) {
    return [];
  }
  const rows: React.ReactElement[] = [
    <NodeRow
      key={id}
      node={node}
      recordCount={recordCountById.get(id) ?? 0}
      expanded={expanded.has(id)}
      selected={id === selectedPath}
      toggle={toggle}
      onSelect={onSelect}
      searchText={searchText}
    />,
  ];
  if (expanded.has(id)) {
    for (const childId of index.getChildren(id)) {
      rows.push(
        ...renderRows(
          index,
          childId,
          recordCountById,
          expanded,
          toggle,
          selectedPath,
          onSelect,
          searchText,
        ),
      );
    }
  }
  return rows;
}

interface NodeRowProps {
  node: SessionMetricNode;
  /** Total records in this node's subtree — the branch "Records" badge. */
  recordCount: number;
  expanded: boolean;
  selected: boolean;
  toggle: (id: string) => void;
  onSelect: (id: string) => void;
  searchText: string;
}

function NodeRow({
  node,
  recordCount,
  expanded,
  selected,
  toggle,
  onSelect,
  searchText,
}: NodeRowProps) {
  const isBranch = node.hasChildren;
  const isSelectable = node.isLeaf;
  const latest = latestRecord(node.data);

  // A leaf-and-folder selects on row click; the chevron handles expansion.
  const handleRowClick = () => {
    if (isSelectable) {
      onSelect(node.id);
    } else if (isBranch) {
      toggle(node.id);
    }
  };

  const className = [
    styles.nodeRow,
    isBranch ? null : styles.leafRow,
    isSelectable || isBranch ? styles.clickableRow : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <TableRow
      hover
      selected={selected}
      onClick={handleRowClick}
      className={className}
    >
      <TableCell>
        <Box
          className={styles.nameCell}
          style={{ paddingLeft: `calc(${node.depth} * var(--spacing-md))` }}
        >
          {isBranch ? (
            <IconButton
              size="small"
              aria-label={expanded ? "Collapse" : "Expand"}
              aria-expanded={expanded}
              onClick={(event) => {
                event.stopPropagation();
                toggle(node.id);
              }}
              className={styles.chevron}
            >
              {expanded ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <span className={styles.chevronSpacer} aria-hidden="true" />
          )}
          <Box className={styles.labels}>
            <span>
              <SubstringHighlight
                text={node.id}
                query={searchText}
                partial
                slice={[node.id.length - node.label.length, node.id.length]}
                markClassName={styles.highlight}
              />
            </span>
            {node.isLeaf && (
              <code className={styles.path}>
                <SubstringHighlight
                  text={node.id}
                  query={searchText}
                  partial
                  markClassName={styles.highlight}
                />
              </code>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell align="right">{isBranch ? recordCount : EM_DASH}</TableCell>
      <TableCell align="right">{latest ? latest.value : EM_DASH}</TableCell>
      <TableCell>
        {latest ? <FormattedDateTime value={latest.published} /> : EM_DASH}
      </TableCell>
    </TableRow>
  );
}
